import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/NumberInput';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Package, Search, Loader2, CheckCircle, Truck,
  FileText, ClipboardCheck, History, Eye, Save, Upload,
  Printer, ListChecks, DollarSign, AlertTriangle, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtFecha } from '../lib/utils'; // Iter 292.g — formato DD-MM-YYYY
import { useAuth } from '../lib/auth';
import { SelloRecepcionBox } from '../components/SelloRecepcionBox';

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

const TIPOS_DOC = [
  { val: 'FA', label: 'Factura' },
  { val: 'GD', label: 'Guía Despacho' },
  { val: 'NC', label: 'Nota Crédito' },
  { val: 'ND', label: 'Nota Débito' },
  { val: 'BO', label: 'Boleta' },
];

export default function RecepcionOC() {
  const { user } = useAuth();
  const role = (user?.rol || user?.role || '').toLowerCase();
  const canEditSello = ['admin', 'administrador', 'gerencia', 'compras', 'bodega', 'bodega_admin', 'jefe_bodega'].includes(role);
  const [ocInput, setOcInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [tipoDoc, setTipoDoc] = useState('FA');
  const [numDoc, setNumDoc] = useState('');
  const [responsable, setResponsable] = useState('Bodega');
  const [guiaDespacho, setGuiaDespacho] = useState('');
  const [destino, setDestino] = useState('bodega'); // bodega | directo_ot

  // Items to receive
  const [cantidades, setCantidades] = useState({});
  const [showOcDetail, setShowOcDetail] = useState(false);
  const [archivoScan, setArchivoScan] = useState(null);

  // Punto 5: Monto factura
  const [montoFactura, setMontoFactura] = useState('');
  const [montoDiff, setMontoDiff] = useState(null);
  const [checkingMonto, setCheckingMonto] = useState(false);

  // Punto 7: Facturas asociadas
  const [facturasAsociadas, setFacturasAsociadas] = useState(null);
  // Facturas disponibles (DTE del proveedor sin asociar)
  const [facturasDisponibles, setFacturasDisponibles] = useState(null);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [dtePreview, setDtePreview] = useState(null);

  const handleBuscarOC = useCallback(async () => {
    const num = ocInput.trim();
    if (!num) return;
    setLoading(true);
    setData(null);
    setCantidades({});
    setMontoDiff(null);
    setFacturasAsociadas(null);
    setFacturasDisponibles(null);
    setMontoFactura('');
    setDtePreview(null);
    try {
      const res = await api.get(`/bodega/buscar-oc/${num}`);
      setData(res.data);
      const cant = {};
      for (const item of res.data.items || []) {
        if (item.cantidad_pendiente > 0) {
          cant[item.item] = item.cantidad_pendiente;
        }
      }
      setCantidades(cant);

      // Load associated + available invoices in parallel
      try {
        const [facRes, dispRes] = await Promise.all([
          api.get(`/bodega/oc/${num}/facturas-asociadas`),
          api.get(`/bodega/oc/${num}/facturas-disponibles`),
        ]);
        setFacturasAsociadas(facRes.data);
        setFacturasDisponibles(dispRes.data);
      } catch {
        // silently fail
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OC no encontrada');
    } finally {
      setLoading(false);
    }
  }, [ocInput]);

  // Iter 236.m.16 (Carlos): permite navegar desde "OC Pendientes Recepción"
  // con `?oc=12345` y auto-buscar la OC al cargar la página.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const ocParam = searchParams.get('oc');
    if (ocParam && ocParam.trim() && !data && !loading) {
      setOcInput(ocParam.trim());
      // Disparar búsqueda en el siguiente tick para que setOcInput surta efecto
      setTimeout(() => {
        const ev = new CustomEvent('chs-buscar-oc-auto', { detail: { oc: ocParam.trim() } });
        window.dispatchEvent(ev);
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const handler = (e) => {
      const oc = e.detail?.oc;
      if (oc) {
        setOcInput(oc);
        // Use the latest handleBuscarOC via the dependency on ocInput
        // pero como ocInput ya está seteado, llamamos directo
        (async () => {
          setLoading(true);
          setData(null);
          try {
            const res = await api.get(`/bodega/buscar-oc/${oc}`);
            setData(res.data);
            const cant = {};
            for (const item of res.data.items || []) {
              if (item.cantidad_pendiente > 0) cant[item.item] = item.cantidad_pendiente;
            }
            setCantidades(cant);
            try {
              const [facRes, dispRes] = await Promise.all([
                api.get(`/bodega/oc/${oc}/facturas-asociadas`),
                api.get(`/bodega/oc/${oc}/facturas-disponibles`),
              ]);
              setFacturasAsociadas(facRes.data);
              setFacturasDisponibles(dispRes.data);
            } catch { /* silent */ }
          } catch (err) {
            toast.error(err.response?.data?.detail || 'OC no encontrada');
          } finally {
            setLoading(false);
          }
        })();
      }
    };
    window.addEventListener('chs-buscar-oc-auto', handler);
    return () => window.removeEventListener('chs-buscar-oc-auto', handler);
  }, []);

  const setCantidad = (itemNum, val) => {
    setCantidades(prev => ({ ...prev, [itemNum]: parseFloat(val) || 0 }));
  };

  // Punto 4: Recepcionar Todo
  const handleRecepcionarTodo = () => {
    if (!data?.items) return;
    const cant = {};
    for (const item of data.items) {
      if (item.cantidad_pendiente > 0) {
        cant[item.item] = item.cantidad_pendiente;
      }
    }
    setCantidades(cant);
    toast.success(`${Object.keys(cant).length} ítems marcados para recepcionar`);
  };

  // Limpiar cantidades
  const handleLimpiarCantidades = () => {
    setCantidades({});
  };

  const totalRecepcionar = Object.values(cantidades).filter(v => v > 0).length;
  const montoRecepcionar = (data?.items || []).reduce((sum, item) => {
    const cant = cantidades[item.item] || 0;
    return sum + (cant * item.valor_unitario);
  }, 0);

  // Punto 5: Verificar monto factura
  const handleVerificarMonto = async () => {
    if (!montoFactura || !data?.oc?.numero_oc) return;
    setCheckingMonto(true);
    try {
      const res = await api.post('/bodega/verificar-monto-factura', {
        numero_oc: data.oc.numero_oc,
        monto_factura: parseFloat(montoFactura) || 0,
      });
      setMontoDiff(res.data);
    } catch (err) {
      toast.error('Error al verificar monto');
    } finally {
      setCheckingMonto(false);
    }
  };

  const handleAsociarFactura = async (factura) => {
    try {
      await api.put(`/bodega/oc/${data.oc.numero_oc}/asociar-factura`, {
        dte_id: factura.id, folio: String(factura.folio),
      });
      toast.success(`Factura ${factura.folio} asociada`);
      // Auto-fill numero_documento with folio
      setNumDoc(String(factura.folio));
      setTipoDoc('FA');
      // Reload both lists
      const [facRes, dispRes] = await Promise.all([
        api.get(`/bodega/oc/${data.oc.numero_oc}/facturas-asociadas`),
        api.get(`/bodega/oc/${data.oc.numero_oc}/facturas-disponibles`),
      ]);
      setFacturasAsociadas(facRes.data);
      setFacturasDisponibles(dispRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al asociar');
    }
  };

  const handleDesasociarFactura = async (factura) => {
    try {
      await api.put(`/bodega/oc/${data.oc.numero_oc}/desasociar-factura`, {
        dte_id: factura.id, folio: String(factura.folio),
      });
      toast.success(`Factura ${factura.folio} desasociada`);
      const [facRes, dispRes] = await Promise.all([
        api.get(`/bodega/oc/${data.oc.numero_oc}/facturas-asociadas`),
        api.get(`/bodega/oc/${data.oc.numero_oc}/facturas-disponibles`),
      ]);
      setFacturasAsociadas(facRes.data);
      setFacturasDisponibles(dispRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desasociar');
    }
  };

  const handleGuardar = async () => {
    if (!numDoc.trim()) {
      toast.error('Ingrese el número de documento');
      return;
    }
    if (totalRecepcionar === 0) {
      toast.error('No hay ítems para recepcionar');
      return;
    }

    setSaving(true);
    try {
      const items = (data?.items || [])
        .filter(item => (cantidades[item.item] || 0) > 0)
        .map(item => ({
          item: item.item,
          descripcion: item.descripcion,
          codigo_material: item.codigo_material,
          cantidad_pedida: item.cantidad_pedida,
          cantidad_recepcionada: cantidades[item.item] || 0,
          valor_unitario: item.valor_unitario,
          total: (cantidades[item.item] || 0) * item.valor_unitario,
          ot_numero: item.ot_numero ? parseInt(item.ot_numero) || null : null,
        }));

      const res = await api.post('/bodega/recepcionar', {
        numero_oc: data.oc.numero_oc,
        fecha,
        tipo_documento: tipoDoc,
        numero_documento: numDoc,
        responsable,
        guia_despacho: guiaDespacho,
        destino,
        items,
      });

      const stockMsg = res.data.stock_actualizado > 0 ? ` (${res.data.stock_actualizado} items actualizaron stock)` : '';
      toast.success(res.data.message + stockMsg);

      // Upload scanned document if selected
      if (archivoScan && res.data.numero_recepcion) {
        try {
          const formData = new FormData();
          formData.append('file', archivoScan);
          await api.post(`/bodega/recepcion/${res.data.numero_recepcion}/upload-documento`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Documento escaneado guardado');
        } catch {
          toast.error('Recepción guardada, pero error al subir documento');
        }
      }

      // Reload OC
      const reload = await api.get(`/bodega/buscar-oc/${data.oc.numero_oc}`);
      setData(reload.data);
      setCantidades({});
      setNumDoc('');
      setGuiaDespacho('');
      setArchivoScan(null);
      setMontoFactura('');
      setMontoDiff(null);

      // Reload facturas asociadas
      try {
        const facRes = await api.get(`/bodega/oc/${data.oc.numero_oc}/facturas-asociadas`);
        setFacturasAsociadas(facRes.data);
      } catch { /* silent */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al recepcionar');
    } finally {
      setSaving(false);
    }
  };

  // Punto 6: Imprimir PDF
  const handleImprimirRecepcion = async (numRecepcion) => {
    try {
      const res = await api.get(`/bodega/recepcion/${numRecepcion}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Recepcion_${numRecepcion}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al generar PDF');
    }
  };

  const handleUploadDoc = async (numRecepcion, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/bodega/recepcion/${numRecepcion}/upload-documento`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      if (data?.oc?.numero_oc) {
        const reload = await api.get(`/bodega/buscar-oc/${data.oc.numero_oc}`);
        setData(reload.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error subiendo documento');
    }
  };

  const oc = data?.oc;
  const items = data?.items || [];
  const recPrevias = data?.recepciones_previas || [];

  return (
    <div className="space-y-5" data-testid="recepcion-oc-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Truck className="w-6 h-6 text-emerald-600" />
          Recepción de Orden de Compra
        </h1>
        <p className="text-sm text-muted-foreground">
          Recepcione materiales y servicios por ítem o todo de una vez.
        </p>
      </div>

      {/* Search OC */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1 max-w-xs">
            <label className="block text-xs font-medium mb-1">N° Orden de Compra</label>
            <Input placeholder="Ej: 18150" value={ocInput}
              onChange={(e) => setOcInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleBuscarOC()}
              data-testid="oc-search-input" />
          </div>
          <Button onClick={handleBuscarOC} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="buscar-oc-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Buscar OC
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      )}

      {oc && (
        <>
          {/* OC Header */}
          <div className="bg-white border rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500">OC</p>
                <p className="font-bold text-lg text-emerald-700">#{oc.numero_oc}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Proveedor</p>
                <p className="font-medium">{oc.nombre_proveedor}</p>
                <p className="text-xs text-gray-400">{oc.rut_proveedor}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">OT</p>
                <p className="font-medium">{oc.ot_numero || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total OC</p>
                <p className="font-bold">${fmt(oc.total)}</p>
                <p className="text-xs text-gray-400">Neto: ${fmt(oc.neto)}</p>
              </div>
              <div className="flex items-end">
                <Button variant="outline" size="sm" onClick={() => setShowOcDetail(true)}
                  data-testid="ver-oc-btn">
                  <Eye className="w-4 h-4 mr-1" /> Ver OC Completa
                </Button>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500 flex gap-4">
              <span>Items: {data.total_items}</span>
              <span className="text-emerald-600">Completos: {data.items_completos}</span>
              <span className="text-amber-600">Pendientes: {data.total_items - data.items_completos}</span>
            </div>
          </div>

          {/* Sello de Recepción (Iter 411) */}
          {data.sello_recepcion && (
            <SelloRecepcionBox
              sello={data.sello_recepcion}
              editable={canEditSello}
              numeroOc={oc.numero_oc}
              onUpdated={(s) => setData((d) => ({ ...d, sello_recepcion: s }))}
              variant="card"
            />
          )}

          {/* Punto 7: Facturas asociadas a la OC */}
          {facturasAsociadas && facturasAsociadas.cantidad > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="facturas-asociadas">
              <h3 className="font-semibold text-sm text-blue-800 mb-2 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Facturas Asociadas a esta OC ({facturasAsociadas.cantidad})
              </h3>
              <div className="space-y-1">
                {facturasAsociadas.facturas.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded px-3 py-1.5 text-xs">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold">{f.folio}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${f.tipo?.includes('Factura') ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                        {f.tipo}
                      </span>
                      <span className="text-gray-500">{f.emisor}</span>
                      <span className="text-gray-400">{f.fecha}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">${fmt(f.total)}</span>
                      {f.detalle && f.detalle.length > 0 && (
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]"
                          onClick={() => setDtePreview({...f, emisor_nombre: f.emisor, fecha_emision: f.fecha})}>
                          <Eye className="w-3 h-3" />
                        </Button>
                      )}
                      {f.asociada_manual && (
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-red-500 hover:text-red-700"
                          onClick={() => handleDesasociarFactura(f)}>
                          Quitar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between text-xs border-t border-blue-200 pt-2">
                <span className="text-blue-700 font-medium">Total facturado: ${fmt(facturasAsociadas.total_facturas)}</span>
                <span className="text-blue-700">Total OC: ${fmt(facturasAsociadas.total_oc)}</span>
                {Math.abs(facturasAsociadas.diferencia) > 1 && (
                  <span className={`font-bold flex items-center gap-1 ${facturasAsociadas.diferencia > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                    <AlertTriangle className="w-3 h-3" />
                    Diferencia: ${fmt(facturasAsociadas.diferencia)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Facturas disponibles del proveedor (sin asociar) */}
          {facturasDisponibles && facturasDisponibles.total > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="facturas-disponibles">
              <h3 className="font-semibold text-sm text-amber-800 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Facturas disponibles del proveedor ({facturasDisponibles.total})
              </h3>
              <p className="text-[10px] text-amber-600 mb-2">DTEs del mismo proveedor sin OC asociada. Click para asociar y visualizar.</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {facturasDisponibles.facturas.map((f, i) => (
                  <div key={f.id || i} className={`flex items-center justify-between rounded px-3 py-2 text-xs transition-colors ${f.menciona_oc ? 'bg-green-100 border border-green-300' : 'bg-white border border-amber-100'}`}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="font-mono font-bold text-sm">{f.folio}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${f.tipo === 'Factura' ? 'bg-blue-100 text-blue-700' : f.tipo === 'Guía Despacho' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'}`}>
                        {f.tipo}
                      </span>
                      {f.menciona_oc && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-200 text-green-800">
                          Menciona OC
                        </span>
                      )}
                      <span className="text-gray-500 truncate">{fmtFecha(f.fecha_emision)}</span>
                      <span className="font-mono font-medium">${fmt(f.total)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 ml-2 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px]"
                        onClick={() => setDtePreview(f)} data-testid={`ver-dte-${f.folio}`}>
                        <Eye className="w-3 h-3 mr-1" /> Ver
                      </Button>
                      <Button size="sm" className="h-7 px-2 text-[10px] bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => handleAsociarFactura(f)} data-testid={`asociar-dte-${f.folio}`}>
                        <ListChecks className="w-3 h-3 mr-1" /> Asociar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* DTE Preview Dialog */}
          <Dialog open={!!dtePreview} onOpenChange={() => setDtePreview(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {dtePreview?.tipo} N° {dtePreview?.folio}
                </DialogTitle>
              </DialogHeader>
              {dtePreview && (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div><span className="text-muted-foreground text-xs">Emisor</span><p className="font-medium">{dtePreview.emisor_nombre || dtePreview.emisor}</p></div>
                    <div><span className="text-muted-foreground text-xs">RUT</span><p className="font-mono">{dtePreview.emisor_rut}</p></div>
                    <div><span className="text-muted-foreground text-xs">Fecha</span><p>{fmtFecha(dtePreview.fecha_emision || dtePreview.fecha)}</p></div>
                    <div><span className="text-muted-foreground text-xs">Total</span><p className="font-bold text-lg">${fmt(dtePreview.total)}</p></div>
                  </div>
                  {dtePreview.menciona_oc && (
                    <div className="bg-green-50 border border-green-200 rounded px-3 py-2 text-xs text-green-800 font-medium">
                      Esta factura menciona la OC {data?.oc?.numero_oc}
                    </div>
                  )}
                  {dtePreview.detalle && dtePreview.detalle.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-xs mb-1">Detalle</h4>
                      <table className="w-full text-xs border-collapse">
                        <thead><tr className="bg-muted/50">
                          <th className="px-2 py-1 text-left">#</th>
                          <th className="px-2 py-1 text-left">Descripción</th>
                          <th className="px-2 py-1 text-right">Cant</th>
                          <th className="px-2 py-1 text-right">P.Unit</th>
                          <th className="px-2 py-1 text-right">Total</th>
                        </tr></thead>
                        <tbody>
                          {dtePreview.detalle.map((d, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-2 py-1">{d.numero_linea || i+1}</td>
                              <td className="px-2 py-1">{d.nombre || d.descripcion}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{d.cantidad}</td>
                              <td className="px-2 py-1 text-right tabular-nums">${fmt(d.precio_unitario)}</td>
                              <td className="px-2 py-1 text-right tabular-nums font-medium">${fmt(d.monto_item)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                {dtePreview && !dtePreview.asociada_manual && (
                  <Button onClick={() => { handleAsociarFactura(dtePreview); setDtePreview(null); }}
                    className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="asociar-from-preview">
                    <ListChecks className="w-4 h-4 mr-1" /> Asociar a OC
                  </Button>
                )}
                <Button variant="outline" onClick={() => setDtePreview(null)}>Cerrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reception form */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <h3 className="font-semibold text-sm text-emerald-800 mb-3 flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4" /> Datos de la Recepción
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Fecha</label>
                <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="recep-fecha" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Tipo Documento</label>
                <select value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-input text-sm" data-testid="recep-tipo">
                  {TIPOS_DOC.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">N° Documento *</label>
                <Input placeholder="N° factura o guía" value={numDoc}
                  onChange={(e) => setNumDoc(e.target.value)} data-testid="recep-numdoc" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Guía Despacho</label>
                <Input placeholder="N° guía" value={guiaDespacho}
                  onChange={(e) => setGuiaDespacho(e.target.value)} data-testid="recep-guia" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Responsable</label>
                <Input value={responsable} onChange={(e) => setResponsable(e.target.value)}
                  data-testid="recep-responsable" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Destino</label>
                <select className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                  value={destino} onChange={(e) => setDestino(e.target.value)} data-testid="recep-destino">
                  <option value="bodega">A Bodega (suma stock)</option>
                  <option value="directo_ot">Directo a OT (no suma stock)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Doc. Escaneado</label>
                <label className={`flex items-center gap-2 h-9 px-3 rounded-md border text-sm cursor-pointer transition-colors ${archivoScan ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-input hover:bg-gray-50'}`}
                  data-testid="recep-upload">
                  <Upload className="w-4 h-4 shrink-0" />
                  <span className="truncate">{archivoScan ? archivoScan.name : 'Cargar PDF'}</span>
                  <input type="file" accept=".pdf,.jpg,.png" className="hidden"
                    onChange={(e) => setArchivoScan(e.target.files?.[0] || null)} />
                </label>
              </div>
            </div>

            {/* Punto 5: Monto factura */}
            <div className="mt-3 flex items-end gap-3 border-t border-emerald-200 pt-3">
              <div className="flex-1 max-w-xs">
                <label className="block text-xs font-medium mb-1 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Monto Total Factura
                </label>
                <NumberInput allowDecimal={false} allowNegative={false} placeholder="Ingrese monto factura" value={montoFactura}
                  onChange={(clean) => { setMontoFactura(clean); setMontoDiff(null); }}
                  onBlur={handleVerificarMonto}
                  data-testid="monto-factura-input" />
              </div>
              <Button variant="outline" size="sm" onClick={handleVerificarMonto} disabled={checkingMonto || !montoFactura}
                data-testid="verificar-monto-btn">
                {checkingMonto ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <DollarSign className="w-3 h-3 mr-1" />}
                Verificar
              </Button>
              {montoDiff && (
                <div className={`text-xs px-3 py-1.5 rounded-md font-medium ${montoDiff.tiene_diferencia ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}
                  data-testid="monto-resultado">
                  {montoDiff.tiene_diferencia ? (
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Diferencia: ${fmt(montoDiff.diferencia)}
                      {montoDiff.cantidad_otras_facturas > 0 && ` (${montoDiff.cantidad_otras_facturas} otras facturas: $${fmt(montoDiff.monto_otras_facturas)})`}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Monto coincide con OC
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Punto 4: Botones masivos + Items table */}
          <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
            {/* Action buttons above table */}
            <div className="bg-gray-50 border-b px-3 py-2 flex items-center justify-between">
              <span className="text-xs text-gray-500">{items.length} ítems en la OC</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleRecepcionarTodo}
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  data-testid="recepcionar-todo-btn">
                  <ListChecks className="w-3.5 h-3.5 mr-1" /> Recepcionar Todo
                </Button>
                <Button variant="ghost" size="sm" onClick={handleLimpiarCantidades}
                  className="text-gray-500" data-testid="limpiar-btn">
                  Limpiar
                </Button>
              </div>
            </div>
            <table className="w-full text-xs" data-testid="items-table">
              <thead className="sticky top-14 lg:top-0 z-20">
                <tr className="bg-gray-800 text-white text-left">
                  <th className="px-3 py-2 w-10">#</th>
                  <th className="px-3 py-2">Descripción</th>
                  <th className="px-3 py-2 text-center">Und</th>
                  <th className="px-3 py-2 text-right">Pedida</th>
                  <th className="px-3 py-2 text-right">Recibida</th>
                  <th className="px-3 py-2 text-right">Pendiente</th>
                  <th className="px-3 py-2 text-right">V. Unit.</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-center w-28">A Recepcionar</th>
                  <th className="px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const cant = cantidades[item.item] || 0;
                  return (
                    <tr key={item.item}
                      className={`border-t transition-colors ${item.completado ? 'bg-green-50/50' : cant > 0 ? 'bg-emerald-50/30' : 'hover:bg-gray-50'}`}
                      data-testid={`item-row-${item.item}`}>
                      <td className="px-3 py-2 font-mono font-bold text-gray-500">{idx + 1}</td>
                      <td className="px-3 py-2" title={item.descripcion}>
                        {item.descripcion}
                        {item.observaciones && <span className="block text-[10px] text-gray-400">{item.observaciones}</span>}
                      </td>
                      <td className="px-3 py-2 text-center text-gray-500">{item.unidad || '-'}</td>
                      <td className="px-3 py-2 text-right font-mono">{item.cantidad_pedida}</td>
                      <td className="px-3 py-2 text-right font-mono text-blue-600">{item.cantidad_recibida || 0}</td>
                      <td className="px-3 py-2 text-right font-mono font-medium">{item.cantidad_pendiente}</td>
                      <td className="px-3 py-2 text-right font-mono">${fmt(item.valor_unitario)}</td>
                      <td className="px-3 py-2 text-right font-mono font-medium">${fmt(item.total_linea)}</td>
                      <td className="px-3 py-2 text-center">
                        {item.completado ? (
                          <span className="text-green-600 text-[10px]">Completo</span>
                        ) : (
                          <NumberInput allowNegative={false}
                            value={cant || ''} placeholder="0"
                            onChange={(clean) => setCantidad(item.item, clean)}
                            className="h-7 w-24 text-xs text-center mx-auto"
                            data-testid={`cant-input-${item.item}`} />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {item.completado ? (
                          <span className="inline-flex items-center gap-0.5 bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-medium">
                            <CheckCircle className="w-3 h-3" /> OK
                          </span>
                        ) : item.cantidad_recibida > 0 ? (
                          <span className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-[10px] font-medium">
                            Parcial
                          </span>
                        ) : (
                          <span className="text-gray-400 text-[10px]">Pendiente</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Action bar */}
          {totalRecepcionar > 0 && (
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 flex items-center justify-between"
              data-testid="action-bar">
              <div>
                <p className="font-medium text-emerald-800">
                  {totalRecepcionar} ítem{totalRecepcionar > 1 ? 's' : ''} a recepcionar
                </p>
                <p className="text-xs text-emerald-600">Monto: ${fmt(montoRecepcionar)}</p>
              </div>
              <Button onClick={handleGuardar} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="guardar-recepcion-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar Recepción
              </Button>
            </div>
          )}

          {/* Previous receptions */}
          {recPrevias.length > 0 && (
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <History className="w-4 h-4" /> Recepciones Anteriores ({recPrevias.length})
              </h3>
              <div className="space-y-2">
                {recPrevias.map((r, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-xs">
                    <div className="flex items-center gap-4">
                      <span className="font-mono font-bold">#{r.numero_recepcion}</span>
                      <span className="text-gray-500">{r.fecha || '-'}</span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${r.tipo_documento === 'FA' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                        {r.tipo_documento}
                      </span>
                      <span>Doc: {r.numero_documento || '-'}</span>
                      <span className="text-gray-500">{r.responsable}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">${fmt(r.monto)}</span>
                      {r.source === 'web' && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Web</span>}
                      {/* Punto 6: Botón imprimir */}
                      <button onClick={() => handleImprimirRecepcion(r.numero_recepcion)}
                        className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50"
                        title="Imprimir comprobante" data-testid={`print-${r.numero_recepcion}`}>
                        <Printer className="w-3.5 h-3.5" />
                      </button>
                      {r.ruta_archivo ? (
                        <Eye className="w-3.5 h-3.5 text-blue-600" />
                      ) : (
                        <label className="cursor-pointer inline-flex items-center gap-1 text-[10px] bg-amber-50 text-amber-700 px-2 py-1 rounded hover:bg-amber-100">
                          <Upload className="w-3 h-3" /> PDF
                          <input type="file" accept=".pdf,.jpg,.png" className="hidden"
                            onChange={(e) => e.target.files?.[0] && handleUploadDoc(r.numero_recepcion, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!data && !loading && (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600 font-medium">Ingrese un N° de OC para comenzar</p>
          <p className="text-sm text-muted-foreground mt-1">Se mostrarán los ítems pendientes de recepción</p>
        </div>
      )}

      {/* OC Detail Modal */}
      <Dialog open={showOcDetail} onOpenChange={setShowOcDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" /> Orden de Compra #{oc?.numero_oc}
            </DialogTitle>
          </DialogHeader>
          {oc && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded space-y-1">
                  <p><strong>Proveedor:</strong> {oc.nombre_proveedor}</p>
                  <p><strong>RUT:</strong> {oc.rut_proveedor}</p>
                  <p><strong>Fecha:</strong> {oc.fecha || '-'}</p>
                  <p><strong>Solicitante:</strong> {oc.solicitante || '-'}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded space-y-1">
                  <p><strong>OT:</strong> {oc.ot_numero || '-'}</p>
                  <p><strong>Neto:</strong> ${fmt(oc.neto)}</p>
                  <p><strong>IVA:</strong> ${fmt(oc.iva)}</p>
                  <p><strong>Total:</strong> <span className="font-bold">${fmt(oc.total)}</span></p>
                  <p><strong>Aprobada:</strong> {oc.aprobada ? 'Sí' : 'No'}</p>
                </div>
              </div>
              {oc.observaciones && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3">
                  <p className="font-semibold text-amber-800 text-xs mb-1">Observaciones</p>
                  <p className="text-sm whitespace-pre-wrap">{oc.observaciones}</p>
                </div>
              )}
              <div>
                <p className="font-semibold mb-2">Detalle de Items ({items.length})</p>
                <table className="w-full text-xs border">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-2 py-1 text-left">#</th>
                      <th className="px-2 py-1 text-left">Descripción</th>
                      <th className="px-2 py-1 text-center">Und</th>
                      <th className="px-2 py-1 text-right">Cant.</th>
                      <th className="px-2 py-1 text-right">V. Unit.</th>
                      <th className="px-2 py-1 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={item.item} className="border-t">
                        <td className="px-2 py-1">{idx + 1}</td>
                        <td className="px-2 py-1">{item.descripcion}</td>
                        <td className="px-2 py-1 text-center">{item.unidad || '-'}</td>
                        <td className="px-2 py-1 text-right">{item.cantidad_pedida}</td>
                        <td className="px-2 py-1 text-right">${fmt(item.valor_unitario)}</td>
                        <td className="px-2 py-1 text-right font-medium">${fmt(item.total_linea)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t bg-gray-50 font-bold">
                      <td colSpan={5} className="px-2 py-1 text-right">Total Neto:</td>
                      <td className="px-2 py-1 text-right">${fmt(oc.neto)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOcDetail(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
